import {
  createUserWithEmailAndPassword,
  getAuth,
  sendPasswordResetEmail,
} from 'firebase/auth';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../../../data/firebase/config';
import { ALLOWED_DOMAINS } from '../../env/allowed-domains';
import { DOCUMENTS_DB } from '../../env/documents-db';
import {
  AdminUserStatus,
  CreateAdminUserData,
  UpdateAdminUserData,
} from '../../types/admin-user';
import { User } from '../../types/user';

export class AdminUserService {
  private collectionRef = collection(db, DOCUMENTS_DB.USERS_ADMIN);
  private usersCollectionRef = collection(db, DOCUMENTS_DB.USERS);
  private auth = getAuth();

  /**
   * Verifica si un usuario existe en Firebase Auth por email
   */
  async checkUserInAuth(
    email: string,
  ): Promise<{ exists: boolean; uid?: string }> {
    try {
      // Nota: Firebase Auth no permite verificar por email directamente desde el cliente
      // Esta verificación se hará intentando crear el usuario y capturando el error
      console.log('🔍 Verificando usuario en Firebase Auth:', email);
      return { exists: false }; // Se determinará durante la creación
    } catch (error) {
      console.error('Error verificando usuario en Auth:', error);
      return { exists: false };
    }
  }

  /**
   * Verifica si un usuario existe en la colección users por email
   */
  async checkUserInUsersCollection(email: string): Promise<User | null> {
    try {
      console.log('🔍 Verificando usuario en users:', email);
      const q = query(
        this.usersCollectionRef,
        where('email', '==', email.toLowerCase()),
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const docSnap = querySnapshot.docs[0];
        const data = docSnap.data();
        console.log('✅ Usuario encontrado en users con ID:', docSnap.id);
        // Importante: usar docSnap.id como id, no el campo id de los datos
        return { ...data, id: docSnap.id } as User;
      }

      console.log('ℹ️ Usuario no encontrado en users');
      return null;
    } catch (error) {
      console.error('Error verificando usuario en users:', error);
      return null;
    }
  }

  /**
   * Verifica si un usuario existe en la colección users_admin
   */
  async checkUserInAdminCollection(email: string): Promise<User | null> {
    try {
      console.log('🔍 Verificando usuario en users_admin:', email);
      const q = query(this.collectionRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() } as User;
      }

      return null;
    } catch (error) {
      console.error('Error verificando usuario en users_admin:', error);
      return null;
    }
  }

  /**
   * Obtiene el estado completo de un usuario (Auth + Admin Collection)
   */
  async getUserStatus(email: string): Promise<AdminUserStatus> {
    try {
      const authStatus = await this.checkUserInAuth(email);
      const adminUser = await this.checkUserInAdminCollection(email);

      return {
        email,
        existsInAuth: authStatus.exists,
        existsInAdminCollection: !!adminUser,
        authUid: authStatus.uid,
        adminUser,
      };
    } catch (error) {
      console.error('Error obteniendo estado del usuario:', error);
      return {
        email,
        existsInAuth: false,
        existsInAdminCollection: false,
      };
    }
  }

  /**
   * Crea un usuario administrativo completo
   */
  async createAdminUser(userData: CreateAdminUserData): Promise<{
    success: boolean;
    uid?: string;
    message: string;
  }> {
    try {
      console.log('🚀 Iniciando creación de usuario admin:', userData.email);

      // 1. Validar dominio de email
      const email = userData.email.toLowerCase();
      const domain = email.split('@')[1] || '';
      if (!ALLOWED_DOMAINS.includes(domain)) {
        return {
          success: false,
          message: 'El dominio del correo no está permitido',
        };
      }

      // 2. Verificar si ya existe en users_admin
      const existingAdmin = await this.checkUserInAdminCollection(email);
      if (existingAdmin) {
        return {
          success: false,
          message: 'El usuario ya existe en la colección de administradores',
        };
      }

      let userUid: string;
      let isNewUser = false;

      // 3. Verificar si el usuario existe en la colección users
      // Usar existingUserId si viene del formulario (ya validado), sino buscar por email
      if (userData.existingUserId) {
        // Usuario existe en users - usar el ID que viene del formulario
        console.log(
          'ℹ️ Usando existingUserId del formulario:',
          userData.existingUserId,
        );
        userUid = userData.existingUserId;
      } else {
        // Buscar por email como fallback
        const existingUser = await this.checkUserInUsersCollection(email);

        if (existingUser) {
          console.log(
            'ℹ️ Usuario encontrado por email en users:',
            existingUser.id,
          );
          userUid = existingUser.id;
        } else {
          // Usuario NO existe - crear en Auth y en users
          isNewUser = true;
          console.log('📝 Usuario nuevo, creando en Auth y users...');

          // Validar que se proporcionó documento
          if (!userData.document) {
            return {
              success: false,
              message:
                'El número de documento es requerido para crear un nuevo usuario',
            };
          }

          // Verificar que el documento no esté duplicado
          const docQuery = query(
            this.usersCollectionRef,
            where('document', '==', userData.document),
          );
          const docSnapshot = await getDocs(docQuery);
          if (!docSnapshot.empty) {
            return {
              success: false,
              message:
                'El número de documento ya está registrado en el sistema',
            };
          }

          // Crear usuario en Firebase Auth (contraseña = documento)
          try {
            console.log('📧 Creando usuario en Firebase Auth...');
            const userCredential = await createUserWithEmailAndPassword(
              this.auth,
              email,
              userData.document,
            );
            userUid = userCredential.user.uid;
            console.log('✅ Usuario creado en Firebase Auth:', userUid);
          } catch (authError: any) {
            if (authError.code === 'auth/email-already-in-use') {
              return {
                success: false,
                message:
                  'El email ya está registrado en Firebase Auth pero no en la colección users. Contacte al administrador.',
              };
            }
            throw authError;
          }

          // Crear documento en colección users
          console.log('📄 Creando documento en users...');
          const usersData = {
            document: userData.document,
            name: userData.name.toLowerCase(),
            lastname: userData.lastname.toLowerCase(),
            email: email,
            department: userData.department?.toLowerCase() || '',
            area: userData.area?.toLowerCase() || '',
            unit: userData.unit || '',
            role: 'employee',
            isEnabled: true,
            isNew: false,
          };

          await setDoc(doc(this.usersCollectionRef, userUid), usersData);
          console.log('✅ Usuario creado en colección users');

          // Enviar email de reset password
          try {
            await sendPasswordResetEmail(this.auth, email);
            console.log('📨 Email de reset password enviado');
          } catch (emailError) {
            console.warn('⚠️ No se pudo enviar email de reset:', emailError);
          }
        }
      }

      // 4. Crear documento en users_admin
      console.log('📄 Creando documento en users_admin...');
      const adminUserData = {
        name: userData.name,
        lastname: userData.lastname,
        email: email,
        document: userData.document || '',
        role: 'administrator',
        roles: userData.roles || [],
        isEnabled: userData.isEnabled ?? true,
        isNew: true,
        createdAt: {
          seconds: Math.floor(Date.now() / 1000),
        },
        updatedAt: {
          seconds: Math.floor(Date.now() / 1000),
        },
      };

      await setDoc(doc(this.collectionRef, userUid), adminUserData);
      console.log('✅ Usuario creado en users_admin con ID:', userUid);

      const message = isNewUser
        ? 'Usuario administrativo creado exitosamente. Se ha enviado un email para establecer la contraseña.'
        : 'Usuario administrativo creado exitosamente.';

      return {
        success: true,
        uid: userUid,
        message,
      };
    } catch (error) {
      console.error('❌ Error creando usuario admin:', error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : 'Error desconocido al crear usuario',
      };
    }
  }

  /**
   * Actualiza un usuario administrativo existente
   */
  async updateAdminUser(
    uid: string,
    userData: UpdateAdminUserData,
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      console.log('🔄 Actualizando usuario admin:', uid);

      const userDoc = doc(this.collectionRef, uid);
      const updateData = {
        ...userData,
        updatedAt: {
          seconds: Math.floor(Date.now() / 1000),
        },
      };

      await updateDoc(userDoc, updateData);
      console.log('✅ Usuario actualizado en users_admin');

      return {
        success: true,
        message: 'Usuario administrativo actualizado exitosamente',
      };
    } catch (error) {
      console.error('❌ Error actualizando usuario admin:', error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : 'Error desconocido al actualizar usuario',
      };
    }
  }

  /**
   * Obtiene todos los usuarios administrativos
   */
  async getAllAdminUsers() {
    try {
      console.log('📋 Obteniendo todos los usuarios admin...');
      const q = query(this.collectionRef, orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);

      const users = querySnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as User,
      );

      console.log(`✅ ${users.length} usuarios admin encontrados`);
      return {
        success: true,
        message: 'Usuarios obtenidos exitosamente',
        data: users,
      };
    } catch (error) {
      console.error('❌ Error obteniendo usuarios admin:', error);
      return {
        success: false,
        message: 'Error al cargar usuarios administrativos',
        data: [],
      };
    }
  }

  /**
   * Obtiene un usuario administrativo por ID
   */
  async getAdminUserById(uid: string): Promise<User | null> {
    try {
      console.log('🔍 Obteniendo usuario admin por ID:', uid);
      const userDoc = doc(this.collectionRef, uid);
      const docSnap = await getDoc(userDoc);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as User;
      }

      return null;
    } catch (error) {
      console.error('❌ Error obteniendo usuario admin por ID:', error);
      return null;
    }
  }

  /**
   * Elimina un usuario administrativo (solo de users_admin, no de Firebase Auth)
   */
  async deleteAdminUser(uid: string): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      console.log('🗑️ Eliminando usuario admin:', uid);

      const userDoc = doc(this.collectionRef, uid);
      await deleteDoc(userDoc);
      console.log('✅ Usuario eliminado de users_admin');

      return {
        success: true,
        message: 'Usuario administrativo eliminado exitosamente',
      };
    } catch (error) {
      console.error('❌ Error eliminando usuario admin:', error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : 'Error desconocido al eliminar usuario',
      };
    }
  }

  /**
   * Habilita/deshabilita un usuario administrativo
   */
  async toggleUserStatus(
    uid: string,
    isEnabled: boolean,
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      console.log(
        `${isEnabled ? '✅' : '❌'} ${isEnabled ? 'Habilitando' : 'Deshabilitando'} usuario:`,
        uid,
      );

      const userDoc = doc(this.collectionRef, uid);
      await updateDoc(userDoc, {
        isEnabled,
        updatedAt: {
          seconds: Math.floor(Date.now() / 1000),
        },
      });

      return {
        success: true,
        message: `Usuario ${isEnabled ? 'habilitado' : 'deshabilitado'} exitosamente`,
      };
    } catch (error) {
      console.error('❌ Error cambiando estado del usuario:', error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : 'Error desconocido al cambiar estado del usuario',
      };
    }
  }
}
